/* istanbul ignore file */

import { useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { useHttpClient } from '@bonusly/http-client';
import { Permission, usePermission } from '@bonusly/who-am-i';

import { ResourcePath } from './ResourcePath';
import { CompanyItem, FeaturedItemResponse, QueryKey } from './types';

interface FeaturedItemsOptions {
	limit?: number;
	queryOptions?: UseQueryOptions<AxiosResponse<FeaturedItemResponse>, unknown, CompanyItem[]>;
}

export const useRetrieveFeaturedItems = ({ limit = 9, queryOptions = {} }: FeaturedItemsOptions = {}) => {
	const canClaimPointsBoost = usePermission(Permission.CanClaimPointsBoost);
	const http = useHttpClient('rcs');

	return useQuery<AxiosResponse<FeaturedItemResponse>, unknown, CompanyItem[]>(
		[QueryKey.CatalogsFeatured, limit],
		() => http.get<FeaturedItemResponse>(ResourcePath.CatalogsFeatured, {
			params: {
				limit,
				...(!canClaimPointsBoost && { exclude: { points_boost: true } }),
			},
		}),
		{
			refetchOnWindowFocus: false,
			retry: false,
			select: ({ data }) => {
				// New format: flat array with labels
				if (Array.isArray(data.data)) {
					return data.data;
				}

				// Legacy format support (for backwards compatibility during transition)
				const { previously_claimed, points_boost, promoted } = data.data;
				const featuredItems = canClaimPointsBoost ? [...points_boost, ...promoted] : [...promoted];

				previously_claimed.forEach((claimedItem) => {
					featuredItems.push({
						...claimedItem,
						item: {
							...claimedItem.item,
							forYou: true,
						},
					});
				});

				return featuredItems;
			},
			...queryOptions,
		}
	);
};

export const useInvalidateFeaturedItems = () => {
	const queryClient = useQueryClient();

	return () => {
		queryClient.invalidateQueries([QueryKey.CatalogsFeatured]);
	};
};
